import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Drawer, Spin, Alert, Typography, Space, Tag, Button, Descriptions, message } from 'antd';
import {
  LinkOutlined,
  FileTextOutlined,
  ToolOutlined,
  AlertOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import HallazgoTracker from 'components/admin/Hallazgos/HallazgoTracker';
import HallazgoFotosGallery from 'components/hallazgos/HallazgoFotosGallery';
import HallazgoFotosUpload from 'components/hallazgos/HallazgoFotosUpload';
import { getHallazgoTrazabilidadApi } from 'api/hallazgoFlujo';
import { setHallazgoComponentApi, getComponentSuggestionApi } from 'api/hallazgoComponent';
import ComponentTreeSelect from 'pages/liftlogic/v2/Components/ComponentTreeSelect';
import { useNavigate } from 'react-router-dom';
import paths from 'assets/routes/paths';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import { drawerPropsFromLevel, fmtDateTime, STATUS_LABEL, STATUS_ANTD_COLOR } from './drawerShared';
import DownloadBudgetButton from './downloads/DownloadBudgetButton';
import DownloadOrderButton from './downloads/DownloadOrderButton';
import HistoriaProblema from './HistoriaProblema';

// Fase 9 del roadmap: drawer de detalle de hallazgo.
// Muestra timeline completo (HallazgoTracker full), metadata, cierre si
// aplica, budgets vinculados historicos + budget actual + order resolucion
// + voucher origen con links clickeables para anidar otro drawer.

const SEVERIDAD_COLOR = {
  BAJA: 'green',
  MEDIA: 'blue',
  ALTA: 'orange',
  CRITICA: 'red',
};

function HallazgoDetailDrawer({ open, idHallazgo, claveGrupo, onClose, level = 0 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fotosRefresh, setFotosRefresh] = useState(0);
  const [fotosCount, setFotosCount] = useState(0);
  const [compId, setCompId] = useState(null);
  const [compSaving, setCompSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const { openBudget, openOrder, openVoucher } = useTrazabilidadDrawer();
  const navigate = useNavigate();

  // Historia del problema: cada detección es un punto de la línea de tiempo.
  // Es lo que convierte "Requiere reparación. ×12" en algo legible.
  const [detecciones, setDetecciones] = useState([]);

  useEffect(() => {
    if (!claveGrupo) {
      setDetecciones([]);
      return undefined;
    }
    let cancelado = false;
    axios
      .get(
        `${API_PATH}/actividades-hallazgo/grupos/${encodeURIComponent(claveGrupo)}/detecciones`,
        {
          headers: { Authorization: getAccessTokenApi() },
        }
      )
      .then((res) => {
        if (!cancelado) setDetecciones(res.data || []);
      })
      .catch(() => {
        if (!cancelado) setDetecciones([]);
      });
    return () => {
      cancelado = true;
    };
  }, [claveGrupo]);

  useEffect(() => {
    if (!open || !idHallazgo) return;
    setLoading(true);
    setError(null);
    setData(null);
    getHallazgoTrazabilidadApi(idHallazgo)
      .then((res) => {
        if (res && res.id) {
          setData(res);
          setCompId(res.id_component ?? null);
          if (res.id_component == null) {
            getComponentSuggestionApi(idHallazgo)
              .then((sg) => setSuggestion(sg?.suggestion || null))
              .catch(() => setSuggestion(null));
          }
        } else setError(res?.message || 'No se pudo cargar el hallazgo');
      })
      .catch((err) => setError(err?.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [open, idHallazgo]);

  const onChangeComponent = async (nodeId) => {
    setCompSaving(true);
    try {
      await setHallazgoComponentApi(idHallazgo, nodeId ?? null);
      setCompId(nodeId ?? null);
      if (nodeId) setSuggestion(null);
      message.success(nodeId ? 'Componente asociado' : 'Componente desasociado');
    } catch (e) {
      message.error(e?.response?.data?.message || 'No se pudo actualizar el componente');
    } finally {
      setCompSaving(false);
    }
  };

  return (
    <Drawer
      {...drawerPropsFromLevel(level)}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnHidden
      title={
        <Space align="center" size="middle">
          <AlertOutlined style={{ color: SEVERIDAD_COLOR[data?.severidad] ?? '#64748b' }} />
          <span>Hallazgo {idHallazgo ? `#${idHallazgo}` : ''}</span>
          {data?.severidad ? (
            <Tag color={SEVERIDAD_COLOR[data.severidad]}>{data.severidad}</Tag>
          ) : null}
          {data?.estado === 'INACTIVA' ? (
            <Tag icon={<CloseCircleFilled />} color="default">
              Cerrado
            </Tag>
          ) : null}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : data ? (
        <>
          {/* Descripcion */}
          <div style={{ marginBottom: 20 }}>
            <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
              DESCRIPCIÓN
            </Typography.Text>
            <Typography.Paragraph style={{ marginTop: 6, marginBottom: 0 }}>
              {data.descripcion || '—'}
            </Typography.Paragraph>
          </div>

          {/* Fotos */}
          <div style={{ marginBottom: 20 }}>
            <Typography.Text
              strong
              style={{
                fontSize: 11,
                letterSpacing: 0.8,
                color: '#64748b',
                display: 'block',
                marginBottom: 8,
              }}
            >
              FOTOS
            </Typography.Text>
            <HallazgoFotosGallery
              idHallazgo={idHallazgo}
              refreshKey={fotosRefresh}
              editable
              onCount={setFotosCount}
            />
            <div style={{ marginTop: 8 }}>
              <HallazgoFotosUpload
                idHallazgo={idHallazgo}
                onUploaded={() => setFotosRefresh((n) => n + 1)}
                currentCount={fotosCount}
              />
            </div>
          </div>

          {/* Timeline completo */}
          <div style={{ marginBottom: 20 }}>
            <Typography.Text
              strong
              style={{
                fontSize: 11,
                letterSpacing: 0.8,
                color: '#64748b',
                display: 'block',
                marginBottom: 12,
              }}
            >
              ETAPA
            </Typography.Text>
            <HallazgoTracker etapa={data.etapa} variant="full" />
          </div>

          {/* Historia del problema */}
          <HistoriaProblema detecciones={detecciones} />

          {/* Metadata */}
          <Descriptions
            column={1}
            size="small"
            bordered
            labelStyle={{ width: 160, background: '#f6f7f9' }}
            style={{ marginBottom: 20 }}
          >
            <Descriptions.Item label="Creado">{fmtDateTime(data.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Actividad">
              {data.actividadNombre || 'Comprobante de emergencia'}
            </Descriptions.Item>
            {data.createdByName || data.createdBy ? (
              <Descriptions.Item label="Detectado por">
                {data.createdByName || data.createdBy}
              </Descriptions.Item>
            ) : null}
            {data.closed_at ? (
              <>
                <Descriptions.Item label="Cerrado">{fmtDateTime(data.closed_at)}</Descriptions.Item>
                {data.closed_by ? (
                  <Descriptions.Item label="Cerrado por">{data.closed_by}</Descriptions.Item>
                ) : null}
                {data.razon_cierre ? (
                  <Descriptions.Item label="Razón cierre">{data.razon_cierre}</Descriptions.Item>
                ) : null}
              </>
            ) : null}
          </Descriptions>

          {/* Componente asociado (opcional, vinculable desde cualquier origen) */}
          <div style={{ marginBottom: 20 }}>
            <Typography.Text
              strong
              style={{
                fontSize: 11,
                letterSpacing: 0.8,
                color: '#64748b',
                display: 'block',
                marginBottom: 8,
              }}
            >
              COMPONENTE ASOCIADO
            </Typography.Text>
            {data.id_ascensor ? (
              <>
                <ComponentTreeSelect
                  equipoId={data.id_ascensor}
                  value={compId}
                  onChange={onChangeComponent}
                  disabled={compSaving}
                />
                {!compId && suggestion ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                    <Tag color="blue">Sugerido</Tag>
                    según la actividad: <strong>{suggestion.name}</strong>{' '}
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0 }}
                      disabled={compSaving}
                      onClick={() => onChangeComponent(suggestion.id)}
                    >
                      Usar sugerencia
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <Typography.Text type="secondary">
                El hallazgo no tiene un ascensor asociado; no se puede vincular un componente.
              </Typography.Text>
            )}
          </div>

          {/* Entidades vinculadas */}
          <Typography.Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.8,
              color: '#64748b',
              display: 'block',
              marginBottom: 10,
            }}
          >
            VINCULADO A
          </Typography.Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            {data.budgetActual ? (
              <Space.Compact style={{ width: '100%' }}>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => openBudget(data.budgetActual.id)}
                  style={{ flex: 1, textAlign: 'left' }}
                >
                  <span style={{ marginRight: 8 }}>Presupuesto actual #{data.budgetActual.id}</span>
                  <Tag color={STATUS_ANTD_COLOR[data.budgetActual.status] ?? 'default'}>
                    {STATUS_LABEL[data.budgetActual.status] ?? data.budgetActual.status}
                  </Tag>
                </Button>
                <DownloadBudgetButton idBudget={data.budgetActual.id} />
              </Space.Compact>
            ) : null}
            {data.orderResolucion ? (
              <Space.Compact style={{ width: '100%' }}>
                <Button
                  icon={<ToolOutlined />}
                  onClick={() => openOrder(data.orderResolucion.id)}
                  style={{ flex: 1, textAlign: 'left' }}
                >
                  ODT #{data.orderResolucion.id}
                  {data.orderResolucion.completed_at ? ' · cerrada' : ' · en curso'}
                </Button>
                <DownloadOrderButton idOrder={data.orderResolucion.id} />
              </Space.Compact>
            ) : null}
            {data.voucherOrigen ? (
              <Button
                block
                icon={<AlertOutlined />}
                onClick={() => openVoucher(data.voucherOrigen.id)}
                style={{ textAlign: 'left' }}
              >
                Voucher origen #{data.voucherOrigen.id}
              </Button>
            ) : null}
            {data.mantencionOrigen ? (
              <Button
                block
                icon={<ToolOutlined />}
                onClick={() => {
                  onClose();
                  navigate(paths.admin.mantenciones.detail(data.mantencionOrigen.id));
                }}
                style={{ textAlign: 'left' }}
              >
                Mantención #{data.mantencionOrigen.id}
              </Button>
            ) : null}
            {!data.budgetActual &&
            !data.orderResolucion &&
            !data.voucherOrigen &&
            !data.mantencionOrigen ? (
              <Typography.Text type="secondary">
                Aún no hay presupuesto, ODT ni voucher vinculados.
              </Typography.Text>
            ) : null}
          </Space>

          {/* Historial de budgets (M:N, puede haber rechazados) */}
          {data.budgetLinks && data.budgetLinks.length > 0 ? (
            <>
              <Typography.Text
                strong
                style={{
                  fontSize: 11,
                  letterSpacing: 0.8,
                  color: '#64748b',
                  display: 'block',
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                HISTORIAL DE PRESUPUESTOS
              </Typography.Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                {data.budgetLinks.map((bl) => (
                  <Space.Compact key={bl.idBudget} style={{ width: '100%' }}>
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => openBudget(bl.idBudget)}
                      style={{ flex: 1, textAlign: 'left' }}
                    >
                      #{bl.idBudget}{' '}
                      <Tag color={STATUS_ANTD_COLOR[bl.status] ?? 'default'}>
                        {STATUS_LABEL[bl.status] ?? bl.status}
                      </Tag>
                    </Button>
                    <DownloadBudgetButton idBudget={bl.idBudget} size="small" />
                  </Space.Compact>
                ))}
              </Space>
            </>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}

export default HallazgoDetailDrawer;
